import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { Trans } from '@lingui/react/macro';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { brands } from 'noddi-types';
import { NavioNoteAttachments, NoddiBasicCard, NoddiButton, useNoteAttachmentUrlRefresh } from 'noddi-ui-web';
import { customerRoutes, formatLicensePlate } from 'noddi-util';
import { LatestTireMeasurements, NavioTireSeasonChip, useAuthContext, useBookingActions } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { TireHotelSvg } from '../../../components/TireHotelSvg';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCarName } from '../../../utils/carName';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { ScheduledTireHotelBooking } from './ScheduledTireHotelBooking';

export const ContractCard = () => {
  const { currentUserGroupId } = useAuthContext();
  const { currentBrand } = useCurrentBrand();
  const showActions = currentBrand !== brands.tronderdekk;

  const {
    data: tireHotels,
    isPending: isTireHotelsPending,
    error: tireHotelsError
  } = noddiAsync.useGet({
    type: URLKeys.getTireHotelsForCustomer,
    input: {
      userGroupId: currentUserGroupId!,
      activeOnly: true
    }
  });
  const tireHotel = tireHotels?.length === 1 ? tireHotels[0] : undefined;
  const customerContractId = tireHotel?.customerContractId;
  const carId = tireHotel?.carId;

  const { data: car, error: _carError } = noddiAsync.useGet({
    type: URLKeys.getUserGroupCar,
    input: { userGroupId: currentUserGroupId!, carId: carId ?? 0 },
    queryConfig: { enabled: carId != null }
  });

  const { data: treadDepth, error: _treadDepthError } = noddiAsync.useGet({
    type: URLKeys.getLatestCarWheelMeasurements,
    input: { carId: carId ?? 0 },
    queryConfig: { enabled: carId != null }
  });

  const {
    data: notesPage,
    isLoading: isNotesLoading,
    error: notesError
  } = noddiAsync.useGet({
    type: URLKeys.getCustomerContractNotesForCustomer,
    input: { customerContractId: customerContractId ?? 0 },
    queryConfig: { enabled: customerContractId != null }
  });
  const notes = notesPage?.results ?? [];
  // The file URLs are signed and expire; a customer who keeps the page open refetches before that.
  useNoteAttachmentUrlRefresh(notes, () => invalidateQuery({ urlKey: URLKeys.getCustomerContractNotesForCustomer }));
  const navigate = useNavigate();

  const { clearStore, addCar } = useBookingActions();

  if (isTireHotelsPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (tireHotelsError) {
    return <ApiErrorPageWithTrans error={[tireHotelsError]} />;
  }

  // We only want to render this card if there is exactly one tire hotel contract
  // For b2b customers we show fleet view always,
  // For normal customers with multiple tire hotel contracts we don't show this card, they just manage via normal booking flow
  if (!tireHotel) {
    return null;
  }

  if (notesError) {
    return <ApiErrorPageWithTrans error={[notesError]} />;
  }

  const licensePlateNumber = tireHotel.licensePlateNumber;
  const carName = car ? getCarName({ car }) : undefined;
  const inStockSeason = tireHotel.inStockSeason;
  const inStockSeasonMeasurements =
    inStockSeason != null ? treadDepth?.seasons.find((entry) => entry.season === inStockSeason)?.positions : undefined;
  const hasTreadMeasurements = (inStockSeasonMeasurements?.length ?? 0) > 0;

  const prePopulateAndNavigate = () => {
    clearStore();
    if (licensePlateNumber) {
      addCar({
        make: '',
        model: '',
        hasTireHotel: true,
        licensePlate: {
          number: licensePlateNumber,
          countryCode: 'NO'
        }
      });
    }

    navigate(customerRoutes.newBookingAddress.getPath());
  };

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        <Trans>Tire hotel</Trans>
      </h2>
      <NoddiBasicCard className='flex h-fit flex-col gap-4'>
        <div className='flex justify-between gap-5'>
          <div className='flex flex-col gap-1'>
            {carName && <p className='font-semibold md:text-lg'>{carName}</p>}
            {licensePlateNumber && (
              <p className={carName ? 'text-sm text-muted-foreground' : 'font-semibold md:text-lg'}>
                {formatLicensePlate(licensePlateNumber)}
              </p>
            )}
            {inStockSeason && (
              <NavioTireSeasonChip
                season={inStockSeason}
                size='sm'
                className='mt-1 self-start'
                translations={getCommonTranslations().seasonBadge}
              />
            )}
          </div>
          <div>
            <TireHotelSvg />
          </div>
        </div>

        {carId != null && inStockSeason != null && hasTreadMeasurements && (
          <LatestTireMeasurements
            carId={carId}
            tireSeason={inStockSeason}
            collapsible
            surface='plain'
            translations={getCommonTranslations().wheelMeasurements}
          />
        )}

        {!isNotesLoading && notes.length > 0 && (
          <div className='flex flex-col gap-4'>
            {notes.map((note) => (
              <div key={note.id} className='flex flex-col gap-2'>
                <p className='font-medium'>{note.title}</p>
                <p className='text-sm text-muted-foreground'>{note.content}</p>
                <NavioNoteAttachments
                  attachments={note.attachments}
                  readOnly
                  translations={getCommonTranslations().notes}
                />
              </div>
            ))}
          </div>
        )}

        {tireHotel.scheduledBooking ? (
          <ScheduledTireHotelBooking scheduledBooking={tireHotel.scheduledBooking} />
        ) : (
          showActions && (
            <div className='flex gap-4'>
              <NoddiButton
                className='flex-1'
                variant='outline'
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  prePopulateAndNavigate();
                }}
              >
                <Trans>End agreement</Trans>
              </NoddiButton>
              <NoddiButton
                className='flex-1'
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  prePopulateAndNavigate();
                }}
              >
                <Trans>Order wheel change</Trans>
              </NoddiButton>
            </div>
          )
        )}
      </NoddiBasicCard>
    </div>
  );
};
